import React, { PropsWithChildren, startTransition, useEffect, useMemo, useRef, useState } from "react";
import { ChatData, ConversationTopic, Inbox } from "common/types/common.type";
import { TOPIC_OPTIONS } from "pages/chat/common/topic-options";

type User = {
  name: string;
  image: string;
  role?: string;
};

export type ProfileDetails = {
  about?: string;
  image: string;
  name: string;
};

type ChatContextProp = {
  user: User;
  inbox: Inbox[];
  activeChat?: Inbox;
  onChangeChat: (chat: Inbox) => void;
  isNewChatOpen: boolean;
  onToggleNewChat: (value: boolean) => void;
  isMobileChatOpen: boolean;
  onOpenChat: () => void;
  onCloseChat: () => void;
  isProfileOpen: boolean;
  profileDetails?: ProfileDetails;
  onCloseProfile: () => void;
  onOpenProfile: (profile: ProfileDetails) => void;
  selectedTopics: ConversationTopic[];
  onToggleTopic: (topic: ConversationTopic) => void;
  onClearTopicFilter: () => void;
};

const initialValue: ChatContextProp = {
  user: { name: "", image: "" },
  inbox: [],
  onChangeChat() {
    throw new Error();
  },
  isNewChatOpen: false,
  onToggleNewChat() {
    throw new Error();
  },
  isMobileChatOpen: false,
  onOpenChat() {
    throw new Error();
  },
  onCloseChat() {
    throw new Error();
  },
  isProfileOpen: false,
  onCloseProfile() {
    throw new Error();
  },
  onOpenProfile() {
    throw new Error();
  },
  selectedTopics: [],
  onToggleTopic() {
    throw new Error();
  },
  onClearTopicFilter() {
    throw new Error();
  },
};

export const ChatContext = React.createContext<ChatContextProp>(initialValue);

export default function ChatProvider(props: PropsWithChildren) {
  const { children } = props;

  const [user, setUser] = useState<User>(initialValue.user);
  const [allInbox, setAllInbox] = useState<Inbox[]>(initialValue.inbox);
  const [activeChat, setActiveChat] = useState<Inbox>();
  const [isNewChatOpen, setIsNewChatOpen] = useState(false);
  const [isMobileChatOpen, setIsMobileChatOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [profileDetails, setProfileDetails] = useState<ProfileDetails>();
  const [selectedTopics, setSelectedTopics] = useState<ConversationTopic[]>([]);
  const pushedMobileHistoryRef = useRef(false);

  const handleChangeChat = (chat: Inbox) => {
    setActiveChat(chat);
  };

  const normalizeMessagePreview = (message?: Inbox["messages"][number]) => ({
    lastMessage: message?.text || message?.linkPreview?.title || message?.linkPreview?.url || "",
    lastMessageFrom: message?.from || "me",
    timestamp: message?.time ?? "",
  });

  const handleToggleTopic = (topic: ConversationTopic) => {
    startTransition(() => {
      setSelectedTopics((currentTopics) => {
        const nextTopics = currentTopics.includes(topic)
          ? currentTopics.filter((currentTopic) => currentTopic !== topic)
          : [...currentTopics, topic];

        return nextTopics.length === TOPIC_OPTIONS.length ? [] : nextTopics;
      });
    });
  };

  const handleClearTopicFilter = () => {
    startTransition(() => {
      setSelectedTopics([]);
    });
  };

  useEffect(() => {
    const loadConversations = async () => {
      const dataUrl = `${process.env.PUBLIC_URL}/data/conversations.json`;
      const response = await fetch(dataUrl);
      const data = (await response.json()) as ChatData;
      const defaultImage = "/assets/images/placeholder.jpeg";

      const normalized = (data.conversations || []).map((conversation, index) => {
        const messages = conversation.messages || [];
        const lastTextMessage =
          [...messages].reverse().find((message) => message.type !== "info") || messages[0];
        const preview = normalizeMessagePreview(lastTextMessage);

        return {
          ...conversation,
          id: conversation.id || `conversation-${index + 1}`,
          image: conversation.image || defaultImage,
          lastMessage: conversation.lastMessage || preview.lastMessage,
          lastMessageFrom: preview.lastMessageFrom,
          timestamp: conversation.timestamp || preview.timestamp,
          messageStatus: conversation.messageStatus || "READ",
          notificationsCount: conversation.notificationsCount ?? 0,
          messages: messages.map((message, messageIndex) => ({
            ...message,
            id: message.id || `${conversation.id || index}-${messageIndex}`,
            type: message.type || "message",
            from: message.from || "me",
            status: message.status || "READ",
          })),
        };
      });

      setUser({
        name: data.me?.name || "",
        image: data.me?.image || "/assets/images/feldstein.png",
        role:
          data.me?.role ||
          "דובר שעבד עבור לשכת ראש הממשלה, ובמוקד ההדלפות שמהן נבנה הארכיון הזה.",
      });
      setAllInbox(normalized);
      setActiveChat(normalized[0]);
    };

    loadConversations().catch(() => {
      setAllInbox([]);
    });
  }, []);

  const filteredInbox = useMemo(() => {
    if (selectedTopics.length === 0) {
      return allInbox;
    }

    return allInbox
      .map((conversation) => {
        const visibleMessages = conversation.messages.filter(
          (message) =>
            message.type === "date" ||
            (!!message.topic && selectedTopics.includes(message.topic))
        );
        const lastVisibleMessage =
          [...visibleMessages].reverse().find((message) => message.type !== "date" && message.type !== "info") ||
          [...visibleMessages].reverse().find((message) => message.type !== "date");

        if (!lastVisibleMessage) {
          return null;
        }

        const preview = normalizeMessagePreview(lastVisibleMessage);

        return {
          ...conversation,
          lastMessage: preview.lastMessage,
          lastMessageFrom: preview.lastMessageFrom,
          timestamp: preview.timestamp || conversation.timestamp,
        };
      })
      .filter((conversation) => conversation !== null) as Inbox[];
  }, [allInbox, selectedTopics]);

  useEffect(() => {
    if (!activeChat) {
      return;
    }

    const hasActiveChat = filteredInbox.some((conversation) => conversation.id === activeChat.id);
    if (!hasActiveChat) {
      setActiveChat(filteredInbox[0]);
    }
  }, [activeChat, filteredInbox]);

  useEffect(() => {
    const handlePopState = () => {
      if (pushedMobileHistoryRef.current) {
        pushedMobileHistoryRef.current = false;
      }
      setIsMobileChatOpen(false);
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  const handleOpenChat = () => {
    if (window.innerWidth <= 900 && !isMobileChatOpen) {
      window.history.pushState({ mobileChat: true }, "");
      pushedMobileHistoryRef.current = true;
    }

    setIsMobileChatOpen(true);
  };

  const handleCloseChat = () => {
    if (window.innerWidth <= 900 && isMobileChatOpen && pushedMobileHistoryRef.current) {
      pushedMobileHistoryRef.current = false;
      window.history.back();
      return;
    }

    setIsMobileChatOpen(false);
  };

  const handleOpenProfile = (profile: ProfileDetails) => {
    setProfileDetails(profile);
    setIsProfileOpen(true);
  };

  const handleCloseProfile = () => {
    setIsProfileOpen(false);
  };

  return (
    <ChatContext.Provider
      value={{
        user,
        inbox: filteredInbox,
        activeChat,
        onChangeChat: handleChangeChat,
        isNewChatOpen,
        onToggleNewChat: setIsNewChatOpen,
        isMobileChatOpen,
        onOpenChat: handleOpenChat,
        onCloseChat: handleCloseChat,
        isProfileOpen,
        profileDetails,
        onCloseProfile: handleCloseProfile,
        onOpenProfile: handleOpenProfile,
        selectedTopics,
        onToggleTopic: handleToggleTopic,
        onClearTopicFilter: handleClearTopicFilter,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}

export const useChatContext = () => React.useContext(ChatContext);
